import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { getMessageUsage_api, MessageUsage } from 'api/messageLimitsApi';
import useBrand from './useBrand';

interface MessageLimitsState {
    // State
    messageUsage: MessageUsage | null;
    isLoadingUsage: boolean;
    hasUsageError: boolean;
    lastFetchTime: number | null;

    // Enforcement modal state
    isEnforcementModalOpen: boolean;

    // Computed getters
    getMessagesRemaining: () => number;
    getNeedsUpgrade: () => boolean;
    getCanConsumeMessage: () => boolean;
    getUsagePercentage: () => number;

    // Actions
    setMessageUsage: (usage: MessageUsage | null) => void;
    setIsLoadingUsage: (loading: boolean) => void;
    setHasUsageError: (hasError: boolean) => void;
    fetchMessageUsage: (brandId?: number, force?: boolean) => Promise<void>;
    refreshMessageUsage: () => Promise<void>;
    clearMessageUsage: () => void;

    // Enforcement actions
    setIsEnforcementModalOpen: (isOpen: boolean) => void;
    checkMessageLimitAndEnforce: (context?: string) => boolean;
    closeEnforcementModal: () => void;
}

const useMessageLimits = create(immer<MessageLimitsState>((set, get) => ({
    // Initial state
    messageUsage: null,
    isLoadingUsage: false,
    hasUsageError: false,
    lastFetchTime: null,

    // Enforcement modal state
    isEnforcementModalOpen: false,

    // Computed getters
    getMessagesRemaining: () => {
        const { messageUsage } = get();
        if (!messageUsage) return 0;
        return Math.max(0, messageUsage.message_limit - messageUsage.messages_used);
    },

    getNeedsUpgrade: () => {
        const remaining = get().getMessagesRemaining();
        return remaining <= 0;
    },

    getCanConsumeMessage: () => {
        const remaining = get().getMessagesRemaining();
        return remaining > 0;
    },

    getUsagePercentage: () => {
        const { messageUsage } = get();
        if (!messageUsage || messageUsage.message_limit === 0) return 0;
        return (messageUsage.messages_used / messageUsage.message_limit) * 100;
    },

    // Actions
    setMessageUsage: (usage) => {
        set(state => {
            state.messageUsage = usage;
            state.lastFetchTime = Date.now();
        });
    },

    setIsLoadingUsage: (loading) => {
        set(state => {
            state.isLoadingUsage = loading;
        });
    },

    setHasUsageError: (hasError) => {
        set(state => {
            state.hasUsageError = hasError;
        });
    },

    fetchMessageUsage: async (brandId?: number, force = false) => {
        const { isLoadingUsage, lastFetchTime } = get();

        // Prevent concurrent requests
        if (isLoadingUsage) return;

        // Debounce requests (don't fetch if we just fetched within 5 seconds unless forced)
        if (!force && lastFetchTime && (Date.now() - lastFetchTime) < 5000) {
            return;
        }

        set(state => {
            state.isLoadingUsage = true;
            state.hasUsageError = false;
        });

        try {
            const response = await getMessageUsage_api(brandId);
            if (response) {
                get().setMessageUsage(response);
            } else {
                set(state => {
                    state.hasUsageError = true;
                });
            }
        } catch (error) {
            set(state => {
                state.hasUsageError = true;
            });
        } finally {
            set(state => {
                state.isLoadingUsage = false;
            });
        }
    },

    refreshMessageUsage: async () => {
        const { currentBrand } = useBrand.getState();
        await get().fetchMessageUsage(currentBrand?.id, true); // Force refresh
    },

    clearMessageUsage: () => {
        set(state => {
            state.messageUsage = null;
            state.isLoadingUsage = false;
            state.hasUsageError = false;
            state.lastFetchTime = null;
        });
    },

    // Enforcement actions
    setIsEnforcementModalOpen: (isOpen) => {
        set(state => {
            state.isEnforcementModalOpen = isOpen;
        });
    },

    checkMessageLimitAndEnforce: (context) => {
        const { getCanConsumeMessage } = get();
        
        if (getCanConsumeMessage()) {
            return true;
        }

        // User has hit their limit, show enforcement modal
        set(state => {
            state.isEnforcementModalOpen = true;
        });
        
        // Optional: Track enforcement event for analytics
        if (context) {
            console.log(`Message limit enforced in context: ${context}`);
        }
        
        return false;
    },

    closeEnforcementModal: () => {
        set(state => {
            state.isEnforcementModalOpen = false;
        });
    }
})));

export default useMessageLimits; 